import type { FC, ReactNode } from "react";
import { clsx } from "clsx";
import type { FontWeightClasses, TextColorClasses } from "./Text";

export type HeadlineSize = "6xl" | "5xl" | "4xl" | "3xl" | "2xl" | "xl" | "lg" | "base";

type Tag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

export type HeadlineProps = {
  size: HeadlineSize;
  children: ReactNode;
  className?: string;
  color?: TextColorClasses;
  fontWeight?: FontWeightClasses;
}

const getClasses = (size: HeadlineSize): string => {
  switch (size) {
    case "base": {
      return "text-base font-area";
    }
    case "lg": {
      return "text-lg font-area";
    }
    case "xl": {
      return "text-xl font-area";
    }
    case "2xl": {
      return "text-xl md:text-2xl font-area-inktrap";
    }
    case "3xl": {
      return "text-2xl md:text-3xl font-area-inktrap";
    }
    case "4xl": {
      return "text-3xl md:text-4xl font-area-inktrap";
    }
    case "5xl": {
      return "text-4xl md:text-5xl font-area-inktrap";
    }
    case "6xl": {
      return "text-5xl md:text-[88px] font-area-inktrap leading-tight";
    }
    default: {
      return "";
    }
  }
};
const getTag = (size: HeadlineSize): Tag => {
  switch (size) {
    case "base": {
      return "p";
    }
    case "lg": {
      return "h6";
    }
    case "xl": {
      return "h5";
    }
    case "2xl": {
      return "h4";
    }
    case "3xl": {
      return "h3";
    }
    case "4xl": {
      return "h2";
    }
    case "5xl": {
      return "h1";
    }
    case "6xl": {
      return "h1";
    }
  }
};

export const Headline: FC<HeadlineProps> = ({
  size,
  children,
  className,
  color = `text-gray-900`,
  fontWeight = "font-semibold",
}) => {
  const Tag: Tag = getTag(size);
  return <Tag className={clsx("tracking-[-0.71px]", fontWeight, getClasses(size), color, className)}>{children}</Tag>;
};
